// src/components/blog/BlogCard.jsx

import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  formatDate,
  getAuthorInitials,
  getBlogImageUrl,
  getCategoryName
} from '../../utils/blogUtils';

const BlogCard = memo(({
  blog,
  variant = 'default',
  isDark = false,
  onImageError
}) => {
  if (!blog) return null;

  const isFeatured = variant === 'featured';
  const imageErrorHandler = () => onImageError?.(blog.featured_image);

  if (isFeatured) {
    return (
      <Link to={`/blog/${blog.slug}`} className="block group">
        <div className={`rounded-2xl overflow-hidden transition-all duration-300 hover:transform hover:scale-[1.01] ${
          isDark
            ? 'bg-white/5 border border-white/10'
            : 'bg-gray-50 border border-gray-200 shadow-lg'
        }`}>
          <div className="grid grid-cols-1 lg:grid-cols-2">

            {/* Image Section */}
            <div className="h-64 lg:h-full relative bg-gradient-to-br from-blue-700/20 to-blue-900/20 overflow-hidden">
              <img
                src={getBlogImageUrl(blog.featured_image, blog.title)}
                alt={blog.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={imageErrorHandler}
                loading="lazy"
              />
            </div>

            {/* Content Section */}
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                  isDark
                    ? 'bg-blue-700/30 text-blue-300'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {getCategoryName(blog.category)}
                </span>

                <span className={`text-xs ${
                  isDark ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  {blog.reading_time || '5'} min read
                </span>
              </div>

              <h3 className={`text-2xl sm:text-3xl font-bold mb-3 group-hover:text-blue-400 transition-colors line-clamp-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {blog.title}
              </h3>

              <p className={`mb-4 line-clamp-3 ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {blog.excerpt}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-700 to-blue-900 flex items-center justify-center text-white font-bold text-sm">
                    {getAuthorInitials(blog.author_name)}
                  </div>

                  <div>
                    <p className={`text-sm font-medium ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {blog.author_name || 'Anonymous'}
                    </p>

                    <p className={`text-xs ${
                      isDark ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {formatDate(blog.published_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 text-blue-500 group-hover:gap-2 transition-all">
                  <span className="text-sm font-medium">Read More</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Default card variant
  return (
    <Link to={`/blog/${blog.slug}`} className="block group">
      <div className={`rounded-xl overflow-hidden transition-all duration-300 hover:transform hover:scale-[1.02] ${
        isDark
          ? 'bg-white/5 border border-white/10 hover:border-blue-700/50'
          : 'bg-white border border-gray-200 hover:border-blue-300 hover:shadow-xl'
      }`}>
        <div className="h-48 overflow-hidden bg-gradient-to-br from-blue-700/20 to-blue-900/20">
          <img
            src={getBlogImageUrl(blog.featured_image, blog.title)}
            alt={blog.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={imageErrorHandler}
            loading="lazy"
          />
        </div>

        <div className="p-5">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
              isDark
                ? 'bg-blue-700/30 text-blue-300'
                : 'bg-blue-100 text-blue-800'
            }`}>
              {getCategoryName(blog.category)}
            </span>

            <span className={`text-xs ${
              isDark ? 'text-gray-500' : 'text-gray-400'
            }`}>
              {blog.reading_time || '5'} min read
            </span>
          </div>

          <h3 className={`text-lg font-bold mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {blog.title}
          </h3>

          <p className={`text-sm mb-3 line-clamp-2 ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {blog.excerpt}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-700 to-blue-900 flex items-center justify-center text-white text-xs font-bold">
                {getAuthorInitials(blog.author_name)}
              </div>

              <div>
                <p className={`text-xs font-medium ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {blog.author_name || 'Anonymous'}
                </p>

                <p className={`text-xs ${
                  isDark ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  {formatDate(blog.published_at)}
                </p>
              </div>
            </div>

            <div className="text-blue-500 group-hover:translate-x-1 transition-transform">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
});

BlogCard.displayName = 'BlogCard';

export default BlogCard;